/**
 * TradeStack - Landing Page
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import './Landing.css';

function Landing() {
  const { isAuthenticated } = useAuth();
  const { platform, tiers, formatCurrency } = useConfig();

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="container">
          <div className="header-content">
            <h1 className="logo">{platform.name || 'TradeStack™'}</h1>
            <nav className="header-nav">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary">
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="btn btn-secondary">
                    Login
                  </Link>
                  <Link to="/register" className="btn btn-primary">
                    Start Free
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <h2 className="hero-title">
            All-in-One Business Management<br />for Contractors
          </h2>
          <p className="hero-subtitle">
            Estimates, invoices, expenses, mileage, and tax tracking — all in one place.
            Stop juggling multiple apps and spreadsheets.
          </p>
          <div className="hero-cta">
            <Link to="/register" className="btn btn-primary btn-lg">
              Start Your Free Trial
            </Link>
            <p className="hero-note">No credit card required. 3 jobs free forever.</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features">
        <div className="container">
          <h3 className="section-title">Everything You Need to Run Your Business</h3>
          <div className="features-grid">
            <div className="feature-card">
              <span className="feature-icon">📝</span>
              <h4>Estimates & Invoices</h4>
              <p>Create professional estimates, convert to invoices, get paid online.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">📷</span>
              <h4>Receipt Scanner</h4>
              <p>Snap photos of receipts. Auto-extract vendor, date, and amount.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">🚗</span>
              <h4>Mileage Tracker</h4>
              <p>Log trips, calculate deductions, compare to actual fuel costs.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">📊</span>
              <h4>Tax Reports</h4>
              <p>Real-time profit/loss. Know exactly how much to set aside for taxes.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">🔨</span>
              <h4>Job Tracking</h4>
              <p>Manage projects from lead to completion. Track costs and time.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">👥</span>
              <h4>Customer CRM</h4>
              <p>Keep customer info organized. See job history at a glance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing">
        <div className="container">
          <h3 className="section-title">Simple, Transparent Pricing</h3>
          <p className="section-subtitle">Start free. Upgrade when you're ready.</p>

          <div className="pricing-grid">
            {tiers.slice(0, 4).map((tier) => (
              <div
                key={tier.id}
                className={`pricing-card ${tier.id === 'pro' ? 'featured' : ''}`}
              >
                {tier.id === 'pro' && <div className="featured-badge">Most Popular</div>}
                <h4 className="tier-name">{tier.name}</h4>
                <div className="tier-price">
                  {tier.monthlyPrice === 0 ? (
                    <span className="price">Free</span>
                  ) : tier.monthlyPrice ? (
                    <>
                      <span className="price">{formatCurrency(tier.monthlyPrice)}</span>
                      <span className="period">/month</span>
                    </>
                  ) : (
                    <span className="price">Custom</span>
                  )}
                </div>
                <ul className="tier-features">
                  <li>{tier.limits?.activeJobs === -1 ? 'Unlimited' : tier.limits?.activeJobs} active jobs</li>
                  <li>{tier.limits?.users === -1 ? 'Unlimited' : tier.limits?.users} user{tier.limits?.users !== 1 ? 's' : ''}</li>
                  {tier.features?.receiptScanner && <li>Receipt scanner</li>}
                  {tier.features?.mileageTracker && <li>Mileage tracker</li>}
                  {tier.features?.taxReports && <li>Tax reports</li>}
                  {tier.features?.quickbooksSync && <li>QuickBooks sync</li>}
                  {tier.features?.subcontractorMgmt && <li>Subcontractor management</li>}
                </ul>
                <Link
                  to="/register"
                  className={`btn ${tier.id === 'pro' ? 'btn-primary' : 'btn-outline'} w-full`}
                >
                  {tier.monthlyPrice === 0 ? 'Start Free' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <h3>Ready to simplify your business?</h3>
          <p>Join thousands of contractors who save hours every week.</p>
          <Link to="/register" className="btn btn-primary btn-lg">
            Start Your Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} {platform.name || 'TradeStack™'}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
